import { useState, useEffect } from 'react';
import apiClient from '../api/client';
import { usePermissions } from '../context/PermissionsContext';
import DepartmentGLAssignments from '../components/DepartmentGLAssignments';
import DepartmentApprovalManagement from '../components/DepartmentApprovalManagement';
import DepartmentMembers from '../components/DepartmentMembers';
import TablePagination from '../components/TablePagination';
import { Search, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

const DEPT_PAGE_SIZE = 25;

function SortTh({ label, sortKey, currentKey, currentDir, onSort }) {
  const active = currentKey === sortKey;
  const Icon = active ? (currentDir === 'ASC' ? ChevronUp : ChevronDown) : ChevronsUpDown;
  return (
    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase cursor-pointer hover:bg-gray-100 select-none" onClick={() => onSort(sortKey)}>
      <span className="inline-flex items-center gap-1">{label}<Icon className={`w-3.5 h-3.5 ${active ? 'text-gray-700' : 'text-gray-400'}`} /></span>
    </th>
  );
}

export default function DepartmentsList() {
  const { hasPermission } = usePermissions();
  const canManageFinance = hasPermission('manage_finance_settings');
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [showGLModal, setShowGLModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [selectedDept, setSelectedDept] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortKey, setSortKey] = useState('DepartmentName');
  const [sortDir, setSortDir] = useState('ASC');
  const [deptPage, setDeptPage] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    parentDepartmentId: ''
  });

  useEffect(() => {
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/departments');
      setDepartments(response.data.departments || []);
    } catch (err) {
      console.error('Failed to fetch departments:', err);
      setError('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingDept(null);
    setFormData({ name: '', description: '', parentDepartmentId: '' });
    setShowModal(true);
  };

  const handleEdit = (dept) => {
    setEditingDept(dept);
    setFormData({
      name: dept.DepartmentName,
      description: dept.Description || '',
      parentDepartmentId: dept.ParentDepartmentID || ''
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDept) {
        await apiClient.put(`/departments/${editingDept.DepartmentID}`, formData);
      } else {
        await apiClient.post('/departments', formData);
      }
      setShowModal(false);
      fetchDepartments();
    } catch (err) {
      console.error('Failed to save department:', err);
      alert(err.response?.data?.message || 'Failed to save department');
    }
  };

  const handleDelete = async (deptId, deptName) => {
    if (!confirm(`Are you sure you want to delete "${deptName}"?`)) return;
    
    try {
      await apiClient.delete(`/departments/${deptId}`);
      fetchDepartments();
    } catch (err) {
      console.error('Failed to delete department:', err);
      alert(err.response?.data?.message || 'Failed to delete department');
    }
  };

  const handleManageGLs = (dept) => {
    setSelectedDept(dept);
    setShowGLModal(true);
  };

  const handleManageApprovals = (dept) => {
    setSelectedDept(dept);
    setShowApprovalModal(true);
  };

  const handleManageMembers = (dept) => {
    setSelectedDept(dept);
    setShowMembersModal(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading departments...</div>
      </div>
    );
  }

  const filteredDepts = departments.filter(d => {
    if (!searchTerm) return true;
    const t = searchTerm.toLowerCase();
    return (d.DepartmentName && d.DepartmentName.toLowerCase().includes(t)) ||
           (d.Description && d.Description.toLowerCase().includes(t));
  });
  const sortedDepts = [...filteredDepts].sort((a, b) => {
    const av = a[sortKey], bv = b[sortKey];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    const cmp = typeof av === 'string' ? av.localeCompare(bv, undefined, {sensitivity:'base'}) : (av - bv);
    return sortDir === 'ASC' ? cmp : -cmp;
  });
  const pagedDepts = sortedDepts.slice((deptPage - 1) * DEPT_PAGE_SIZE, deptPage * DEPT_PAGE_SIZE);
  function handleSort(key) {
    if (sortKey === key) setSortDir(d => d === 'ASC' ? 'DESC' : 'ASC');
    else { setSortKey(key); setSortDir('ASC'); }
    setDeptPage(1);
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Departments</h1>
        <button onClick={handleCreate} className="btn btn-primary">Add Department</button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4"><p className="text-red-800">{error}</p></div>
      )}

      {/* Search */}
      <div className="relative w-full md:w-80">
        <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={e => { setSearchTerm(e.target.value); setDeptPage(1); }}
          placeholder="Search departments…"
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <SortTh label="Department Name" sortKey="DepartmentName" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
              <SortTh label="Description" sortKey="Description" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
              <SortTh label="Users" sortKey="UserCount" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
              <SortTh label="Status" sortKey="IsActive" currentKey={sortKey} currentDir={sortDir} onSort={handleSort} />
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {pagedDepts.length > 0 ? (
              pagedDepts.map((dept) => (
                <tr key={dept.DepartmentID} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">
                    {dept.DepartmentName}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {dept.Description || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {dept.UserCount || 0} users
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs font-medium rounded ${
                      dept.IsActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {dept.IsActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-sm space-x-2">
                    <button
                      onClick={() => handleManageMembers(dept)}
                      className="text-indigo-600 hover:text-indigo-800 font-medium"
                      title="View and manage department members"
                    >
                      Members
                    </button>
                    {canManageFinance && (
                      <button
                        onClick={() => handleManageGLs(dept)}
                        className="text-green-600 hover:text-green-800 font-medium"
                      >
                        GL Accounts
                      </button>
                    )}
                    {canManageFinance && (
                      <button
                        onClick={() => handleManageApprovals(dept)}
                        className="text-purple-600 hover:text-purple-800 font-medium"
                        title="Manage approval hierarchy and thresholds"
                      >
                        Approvals
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(dept)}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(dept.DepartmentID, dept.DepartmentName)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                  {searchTerm ? `No departments matching "${searchTerm}"` : 'No departments found. Click "Add Department" to create one.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <TablePagination page={deptPage} pageSize={DEPT_PAGE_SIZE} total={sortedDepts.length} onPageChange={setDeptPage} />
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-gray-900 mb-4">
              {editingDept ? 'Edit Department' : 'Create Department'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Department Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows="3"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Department
                </label>
                <select
                  value={formData.parentDepartmentId}
                  onChange={(e) => setFormData({ ...formData, parentDepartmentId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">None</option>
                  {departments
                    .filter(d => !editingDept || d.DepartmentID !== editingDept.DepartmentID)
                    .map(d => (
                      <option key={d.DepartmentID} value={d.DepartmentID}>
                        {d.DepartmentName}
                      </option>
                    ))}
                </select>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  className="btn btn-primary flex-1"
                >
                  {editingDept ? 'Update' : 'Create'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* GL Accounts Assignment Modal */}
      {showGLModal && selectedDept && (
        <DepartmentGLAssignments
          department={selectedDept}
          onClose={() => {
            setShowGLModal(false);
            setSelectedDept(null);
          }}
        />
      )}

      {/* Approval Hierarchy Modal */}
      {showApprovalModal && selectedDept && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <DepartmentApprovalManagement
            departmentId={selectedDept.DepartmentID}
            onClose={() => {
              setShowApprovalModal(false);
              setSelectedDept(null);
            }}
          />
        </div>
      )}

      {/* Members Modal */}
      {showMembersModal && selectedDept && (
        <DepartmentMembers
          department={selectedDept}
          onClose={() => {
            setShowMembersModal(false);
            fetchDepartments(); // refresh UserCount
            setSelectedDept(null);
          }}
        />
      )}
    </div>
  );
}
